/* Display only. Checkout/warehouse timestamps remain the fulfillment authority. */
(function () {
  'use strict';
  const Clock = window.SownShippingClock;
  if (!Clock || window.sownShippingCountdownInstalled) return;
  window.sownShippingCountdownInstalled = true;

  let config;
  let closedDates;
  let anchor = null;
  let request = null;
  let generation = 0;
  let refreshTimer;
  let lastActive = false;
  const availability = new Map();

  function configure() {
    try {
      config = JSON.parse(document.getElementById('SownShippingConfig').textContent);
      closedDates = Clock.parseClosedDates(config.closedDates);
      return config.enabled === true && ['index', 'product'].includes(config.pageType)
        && closedDates !== null && typeof config.cartUrl === 'string';
    } catch (_) {
      config = null;
      return false;
    }
  }

  function productState() {
    const slot = Array.from(document.querySelectorAll('[data-sown-shipping-product]'))
      .find((node) => !node.closest('quick-add-modal'));
    const form = slot && document.getElementById(slot.dataset.productFormId);
    return {
      slot, form,
      variant: form?.querySelector('[name="id"]'),
      button: form?.querySelector('[name="add"]'),
      picker: slot?.closest('.product-buybox')?.querySelector('variant-radios, variant-selects'),
    };
  }

  function seedAvailability() {
    const { variant, button } = productState();
    if (variant && button) availability.set(variant.value, !button.disabled);
  }

  function productEligible() {
    if (config.pageType !== 'product') return true;
    const { variant, button, picker } = productState();
    // Native variant fetches and cart submissions temporarily disable the button.
    // Availability comes from server-rendered product state, not that busy state.
    if (picker && 'currentVariant' in picker
      && String(picker.currentVariant?.id) !== variant?.value) return false;
    return !!(variant && button && availability.get(variant.value) !== false
      && config.variants && config.variants[variant.value] === true);
  }

  function updateAvailability(event) {
    const { form, variant } = productState();
    const data = event?.data;
    // A native form field named "id" shadows HTMLFormElement.id.
    if (!data || form?.getAttribute('id') !== `product-form-${data.sectionId}`
      || variant?.value !== String(data.variant?.id)) return;
    const button = data.html?.getElementById(`ProductSubmitButton-${data.sectionId}`);
    availability.set(variant.value, !!button && !button.disabled);
    render();
  }

  function remainingText(ms) {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'less than 1 min';
    return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes} min`;
  }

  function render() {
    const now = anchor ? Clock.readClock(anchor, performance.now(), Date.now()) : NaN;
    const shipping = Clock.getWindow(now, closedDates);
    const eligible = !!(config && config.enabled && !document.hidden && navigator.onLine !== false
      && productEligible());
    const active = eligible && shipping.active;
    const mainSlot = productState().slot;
    const dispatchVisible = eligible && config.pageType === 'product'
      && ['active', 'cutoff', 'weekend', 'holiday'].includes(shipping.reason);
    document.querySelectorAll('[data-sown-dispatch]').forEach((note) => {
      const visible = dispatchVisible && !!mainSlot && note.closest('.product-buybox') === mainSlot.closest('.product-buybox');
      if (note.hidden === visible) note.hidden = !visible;
      if (visible) {
        const text = active ? 'Ships today' : 'Ships next business day';
        const label = note.querySelector('[data-sown-dispatch-text]');
        if (label && label.textContent !== text) label.textContent = text;
      }
    });
    document.querySelectorAll('[data-sown-shipping]').forEach((slot) => {
      const visible = active && (!slot.hasAttribute('data-sown-shipping-product')
        || (config.pageType === 'product' && slot === mainSlot));
      if (slot.hidden === visible) slot.hidden = !visible;
      if (visible) {
        const text = remainingText(shipping.remainingMs);
        slot.querySelectorAll('[data-sown-shipping-time]').forEach((node) => {
          if (node.textContent !== text) node.textContent = text;
        });
      }
    });
    document.querySelectorAll('[data-sown-announcement-slot]').forEach((slot) => {
      const original = slot.querySelector('[data-sown-shipping-default]');
      if (original) original.hidden = active;
      slot.closest('.utility-bar')?.toggleAttribute('data-sown-shipping-active', active);
    });
    const status = document.getElementById('SownShippingStatus');
    if (status && active !== lastActive) {
      status.textContent = active
        ? 'Same-day shipping is available for orders completed before noon Eastern.'
        : shipping.reason === 'cutoff' ? 'The same-day shipping cutoff has passed.' : '';
    }
    lastActive = active;
    return shipping;
  }

  function invalidate() {
    generation += 1;
    anchor = null;
    request?.abort();
    request = null;
    clearTimeout(refreshTimer);
    render();
  }

  async function sync() {
    if (request || document.hidden || navigator.onLine === false || !configure()) return;
    // An unverified service worker could synthesize/cachestore a false Date header.
    if (navigator.serviceWorker?.controller) { invalidate(); return; }
    const controller = new AbortController();
    request = controller;
    const version = ++generation;
    const timeout = setTimeout(() => controller.abort(), 1500);
    const started = performance.now();
    try {
      const url = new URL(config.cartUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Unexpected clock origin');
      const nonce = typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
        : Array.from(crypto.getRandomValues(new Uint32Array(4)), (value) => value.toString(16)).join('-');
      url.searchParams.set('_sown_shipping_time', nonce);
      const response = await fetch(url, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store',
        redirect: 'error', signal: controller.signal,
      });
      const receivedPerfMs = performance.now();
      const receivedWallMs = Date.now();
      // Only the HTTP clock headers are needed. Do not parse, retain or log cart data.
      if (response.body) response.body.cancel().catch(() => {});
      const next = Clock.getServerAnchor({
        dateHeader: response.headers.get('Date'), ageHeader: response.headers.get('Age'),
        cacheStatus: response.headers.get('CF-Cache-Status'), contentType: response.headers.get('Content-Type'),
        status: response.status, redirected: response.redirected, url: response.url, origin: location.origin,
        rttMs: receivedPerfMs - started, receivedPerfMs, receivedWallMs,
      });
      if (!next) throw new Error('Unconfirmed shipping clock');
      if (version === generation) anchor = next;
    } catch (_) {
      if (version === generation) anchor = null;
    } finally {
      clearTimeout(timeout);
      if (request === controller) request = null;
      if (version === generation) {
        const shipping = render();
        // Dispatch notes need a fresh anchor after cutoff too (clock TTL is one minute).
        const delay = shipping.active || document.querySelector('[data-sown-dispatch]') || !anchor ? 45000 : 900000;
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(sync, delay);
      }
    }
  }

  function resume() { invalidate(); sync(); }
  document.addEventListener('visibilitychange', () => document.hidden ? invalidate() : resume());
  window.addEventListener('pageshow', (event) => { if (event.persisted) resume(); });
  window.addEventListener('pagehide', invalidate);
  window.addEventListener('online', resume);
  window.addEventListener('offline', invalidate);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.contains(productState().slot)) seedAvailability();
    resume();
  });
  document.addEventListener('change', render);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, updateAvailability);

  }
  setInterval(() => {
    if (anchor) {
      const mono = performance.now() - anchor.perfMs;
      const wall = Date.now() - anchor.wallMs;
      if (mono < 0 || wall < 0 || Math.abs(mono - wall) > 2000) { resume(); return; }
    }
    render();
  }, 250);
  configure();
  seedAvailability();
  render();
  sync();
})();
